import React from 'react';
import { Button } from 'react-bootstrap';
import { Users, PlusCircle, Sparkles, Link2, Briefcase, Unlink, Edit, Trash2, Mail, Send } from 'lucide-react';

// Users section — active users (with employee-link status), pending invitations
// and auto-suggested user↔employee mappings. Data and handlers live in the
// CompanySettings shell (lazy-loaded on first visit); role/link/invite modals
// live in the shell and are opened via the handlers passed here.
const UsersSection = ({
  associatedUsers,
  pendingInvitations,
  mappingSuggestions,
  getMappingForUser,
  canRemoveUser,
  handleApplySuggestion,
  handleUnlinkEmployee,
  handleOpenLinkModal,
  handleOpenRoleModal,
  handleRemoveUser,
  handleResendInvitation,
  handleDeleteInvitation,
  setShowInviteModal,
}) => (
  <div className="modern-card">
    <div className="modern-card-header">
      <div>
        <h2 className="section-title">
          <Users size={24} />
          Användarhantering
        </h2>
        <p className="section-subtitle">
          Hantera teammedlemmar och deras behörigheter
        </p>
      </div>
      <div className="action-buttons">
        <Button
          variant="primary"
          className="d-flex align-items-center gap-2"
          onClick={() => setShowInviteModal(true)}
        >
          <PlusCircle size={16} />
          Bjud in användare
        </Button>
      </div>
    </div>

    <div className="modern-card-body">
      {/* Mapping Suggestions Banner */}
      {mappingSuggestions.length > 0 && (
        <div className="user-section">
          <div style={{
            background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
            border: '1px solid #f59e0b',
            borderRadius: '8px',
            padding: '1rem 1.25rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Sparkles size={16} style={{ color: '#d97706' }} />
              <strong style={{ color: '#92400e', fontSize: '0.875rem' }}>
                Föreslagna kopplingar ({mappingSuggestions.length})
              </strong>
            </div>
            <p style={{ color: '#92400e', fontSize: '0.75rem', marginBottom: '0.75rem' }}>
              Dessa användare och anställda har samma e-postadress och kan kopplas automatiskt.
            </p>
            {mappingSuggestions.map((suggestion, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'white',
                borderRadius: '6px',
                padding: '0.5rem 0.75rem',
                marginBottom: index < mappingSuggestions.length - 1 ? '0.5rem' : 0
              }}>
                <div style={{ fontSize: '0.8rem' }}>
                  <strong>{suggestion.user_name}</strong>
                  <span style={{ color: '#64748b', margin: '0 0.5rem' }}>&rarr;</span>
                  <strong>{suggestion.employee_name}</strong>
                  <span style={{ color: '#64748b', marginLeft: '0.5rem', fontSize: '0.7rem' }}>({suggestion.user_email})</span>
                </div>
                <Button
                  variant="outline-warning"
                  size="sm"
                  className="d-flex align-items-center gap-1"
                  onClick={() => handleApplySuggestion(suggestion)}
                  style={{ fontSize: '0.75rem' }}
                >
                  <Link2 size={12} />
                  Koppla
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Users Section */}
      <div className="user-section" id="aktiva-anvandare">
        <div className="section-header">
          <h3 className="section-heading">
            Aktiva användare ({associatedUsers.length})
          </h3>
        </div>

        {associatedUsers.length > 0 ? (
          <div className="user-grid">
            {associatedUsers.map((user) => {
              const mapping = getMappingForUser(user.user_id);
              return (
                <div key={user.user_id} className="user-card">
                  <div className="user-avatar">
                    {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                  </div>
                  <div className="user-info">
                    <h4 className="user-name">
                      {user.first_name} {user.last_name}
                    </h4>
                    <p className="user-email">{user.email}</p>
                    {user.phone_number && (
                      <p className="user-phone">{user.phone_number}</p>
                    )}
                    <div className="user-roles">
                      {user.roles.map((role, index) => (
                        <span key={index} className="role-badge">
                          {role}
                        </span>
                      ))}
                    </div>
                    {/* Employee link status */}
                    <div style={{ marginTop: '0.5rem' }}>
                      {mapping ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.375rem',
                          fontSize: '0.75rem',
                          color: '#059669',
                          background: '#ecfdf5',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          width: 'fit-content'
                        }}>
                          <Briefcase size={12} />
                          <span>Kopplad: {mapping.firstname} {mapping.lastname}</span>
                        </div>
                      ) : (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.375rem',
                          fontSize: '0.75rem',
                          color: '#94a3b8',
                          fontStyle: 'italic'
                        }}>
                          <Briefcase size={12} />
                          <span>Ej kopplad till anställd</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="user-actions">
                    {mapping ? (
                      <button
                        className="action-btn edit"
                        onClick={() => handleUnlinkEmployee(user.user_id)}
                        title="Koppla bort anställd"
                      >
                        <Unlink size={16} />
                      </button>
                    ) : (
                      <button
                        className="action-btn edit"
                        onClick={() => handleOpenLinkModal(user)}
                        title="Koppla till anställd"
                      >
                        <Link2 size={16} />
                      </button>
                    )}
                    <button
                      className="action-btn edit"
                      onClick={() => handleOpenRoleModal(user)}
                      title="Ändra roller"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className={`action-btn delete ${!canRemoveUser(user.user_id).canRemove ? 'disabled' : ''}`}
                      onClick={() => handleRemoveUser(user.user_id)}
                      title={
                        !canRemoveUser(user.user_id).canRemove
                          ? "Kan inte ta bort sista admin"
                          : "Ta bort användare"
                      }
                      disabled={!canRemoveUser(user.user_id).canRemove}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <Users size={48} className="empty-icon" />
            <h4>Inga användare än</h4>
            <p>Bjud in teammedlemmar för att komma igång</p>
          </div>
        )}
      </div>

      {/* Pending Invitations Section */}
      <div className="user-section" id="inbjudningar">
        <div className="section-header">
          <h3 className="section-heading">
            Väntande inbjudningar ({pendingInvitations.length})
          </h3>
        </div>

        {pendingInvitations.length > 0 ? (
          <>
            <div className="invitation-list">
              {pendingInvitations.map((invitation) => (
                <div key={invitation.invitation_id} className="invitation-card">
                  <div className="invitation-avatar pending">
                    <Mail size={20} />
                  </div>
                  <div className="invitation-info">
                    <h4 className="invitation-email">{invitation.email}</h4>
                    <div className="invitation-meta">
                      <span className="meta-item">
                        Inbjuden: {new Date(invitation.created_at).toLocaleDateString('sv-SE')}
                      </span>
                      <span className="meta-item">
                        Utgår: {new Date(invitation.expires_at).toLocaleDateString('sv-SE')}
                      </span>
                    </div>
                    <div className="invitation-roles">
                      {invitation.roles.map((role, index) => (
                        <span key={index} className="role-badge pending">
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="invitation-actions">
                    <button
                      className="action-btn resend"
                      onClick={() => handleResendInvitation(invitation.invitation_id)}
                      title="Skicka igen"
                    >
                      <Send size={16} />
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDeleteInvitation(invitation.invitation_id)}
                      title="Ta bort inbjudan"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add invite button after the list */}
            <div className="d-flex justify-content-center mt-3">
              <Button
                variant="primary"
                onClick={() => setShowInviteModal(true)}
                className="d-flex align-items-center gap-2"
              >
                <PlusCircle size={16} />
                Bjud in fler användare
              </Button>
            </div>
          </>
        ) : (
          <div className="empty-state small">
            <Mail size={32} className="empty-icon" />
            <h4>Inga väntande inbjudningar</h4>
            <p>Bjud in teammedlemmar för att komma igång</p>
            {/* Add invite button in empty state */}
            <div className="mt-3">
              <Button
                variant="primary"
                onClick={() => setShowInviteModal(true)}
                className="d-flex align-items-center gap-2"
              >
                <PlusCircle size={16} />
                Bjud in användare
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

export default UsersSection;
